import { type DateRange } from '@heidi/design-system/date-range-picker';
import { type SessionStatus } from '@heidi/schema/ml-scribe';
import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';

/**
 * Set of selected session unique IDs - internal tracking for count/clear operations
 */
export const selectedSessionIdsAtom = atom<Set<string>>(new Set<string>());

/**
 * AtomFamily for per-session selection state - O(1) updates
 * Each session gets its own atom, enabling granular subscriptions
 */
export const sessionSelectedAtomFamily = atomFamily((sessionId: string) =>
  atom(
    (get) => get(selectedSessionIdsAtom).has(sessionId),
    (get, set, selected: boolean) => {
      const prev = get(selectedSessionIdsAtom);
      const hasSession = prev.has(sessionId);

      // Skip update if state hasn't changed
      if (selected === hasSession) return;

      const next = new Set(prev);
      if (selected) {
        next.add(sessionId);
      } else {
        next.delete(sessionId);
      }
      set(selectedSessionIdsAtom, next);
    }
  )
);

/**
 * Action atom to clear all selections and purge the atomFamily cache
 */
export const clearAllSelectionsAtom = atom(null, (_get, set) => {
  set(selectedSessionIdsAtom, new Set<string>());
  const params = [...sessionSelectedAtomFamily.getParams()];
  for (const param of params) {
    sessionSelectedAtomFamily.remove(param);
  }
});

/**
 * Sync the atomFamily cache with the currently visible session list.
 * Removes cached atoms for sessions that are neither visible nor selected.
 */
export const syncSessionSelectionFamilyAtom = atom(
  null,
  (get, _set, visibleSessionIds: string[]) => {
    const selectedIds = get(selectedSessionIdsAtom);
    const visibleSet = new Set(visibleSessionIds);
    const paramsToRemove = [...sessionSelectedAtomFamily.getParams()].filter(
      (param) => !visibleSet.has(param) && !selectedIds.has(param)
    );
    for (const param of paramsToRemove) {
      sessionSelectedAtomFamily.remove(param);
    }
  }
);

/**
 * Count of selected sessions - derived atom
 */
export const selectedSessionsCountAtom = atom((get) => get(selectedSessionIdsAtom).size);

/**
 * Whether any sessions are selected - derived atom
 */
export const hasSelectedSessionsAtom = atom(
  (get) => get(selectedSessionIdsAtom).size > 0
);

// Consolidated list filter atoms
export const consolidatedSelectedDateRangeFilterAtom = atom<DateRange>({
  from: undefined,
  to: undefined,
});
export const consolidatedSelectedPatientProfilesFilterAtom = atom<string[]>([]);
export const consolidatedSelectedStatusesFilterAtom = atom<SessionStatus[]>([]);

// Consolidated list sort atoms
export type ConsolidatedSortOption = 'createdAt' | 'updatedAt' | 'name' | 'status';
export type ConsolidatedSortOrder = 'asc' | 'desc';

export const consolidatedSessionsSortByAtom = atom<ConsolidatedSortOption>('createdAt');
export const consolidatedSessionsSortOrderAtom = atom<ConsolidatedSortOrder>('desc');
